import {
  getSectionSchema,
  layoutRendersField,
} from "@a2z/storefront-schemas";

/**
 * The shape these helpers need, and nothing more.
 *
 * Structural rather than the package's `SectionInstance` because the app's own
 * `ThemeSectionInstance` types `blocks` as `unknown[]` — the two disagree on a
 * field none of this touches, and widening either one to satisfy the other
 * would be a change to a type for the sake of a helper that never reads it.
 */
export interface CopySection {
  type: string;
  settings?: Record<string, unknown> | undefined;
}

/**
 * The editable COPY of a storefront section, derived from its schema.
 *
 * A restaurant picks a complete theme and gets that theme's authored words with
 * it — which is right for the composition and wrong for the sentences. Applying
 * STACK shipped "Double up Tuesday … Zamalek & Maadi" and "Ready in 12 minutes"
 * to a restaurant in a different city that makes no such promise, and D15's
 * "no editor" left no way to change it. So the STRUCTURE stays locked and the
 * WORDS open: a seller may edit the text a section declares, and nothing else.
 *
 * The field list is READ OFF THE SCHEMA rather than enumerated here. That is
 * the payoff the theme storage was designed for — a new restaurant section, or
 * a new line on an existing one, becomes editable the moment its schema
 * declares the field, with no second list to keep in step.
 *
 * Only `i18n` text and textarea fields qualify. A colour, a layout switch or a
 * dish reference is composition, not copy, and stays the theme's.
 */

export interface SectionCopyField {
  id: string;
  label: { ar: string; en: string };
  helpText?: { ar: string; en: string };
  /** `textarea` in the schema — the form gives it a taller control. */
  multiline: boolean;
  /** The schema's own `validate.max`, enforced by the pre-publish gate. */
  max?: number;
}

/** A `showIf` is satisfied when every named sibling setting holds one of its values. */
function showIfHolds(
  showIf: Record<string, unknown> | undefined,
  settings: Record<string, unknown>,
): boolean {
  if (!showIf) return true;
  return Object.entries(showIf).every(([key, expected]) => {
    const actual = settings[key];
    return Array.isArray(expected)
      ? (expected as unknown[]).includes(actual)
      : actual === expected;
  });
}

/**
 * The copy fields a section currently offers.
 *
 * `showIf` is evaluated against the section's OWN settings, so a hero set to
 * `plate` does not offer the `split` layout's headline — a field the storefront
 * would never render is not one a seller should be asked to fill in.
 */
export function copyFieldsFor(
  section: CopySection,
  /**
   * The applied skin. A field the skin never reads is a control that does
   * nothing: the seller types into it, the page does not change, and they
   * conclude the editor is broken. Omitted ⇒ no filtering, which is what the
   * pre-editor callers want.
   */
  layout?: string,
): SectionCopyField[] {
  const schema = getSectionSchema(section.type);
  if (!schema) return [];
  const settings = (section.settings ?? {}) as Record<string, unknown>;
  const fields: SectionCopyField[] = [];
  for (const field of schema.settings ?? []) {
    if (field.type !== "text" && field.type !== "textarea") continue;
    if (!("i18n" in field) || !field.i18n) continue;
    if (!showIfHolds(field.showIf, settings)) continue;
    if (layout && !layoutRendersField(layout, section.type, field.id)) continue;
    /*
     * EDITABLE MEANS AUTHORED. A section schema is shared by all six skins, so
     * it declares every line any of them draws — the hero carries VERDE's
     * eyebrow and its three stat pairs, which STACK's hero does not read. A
     * STACK seller offered those got ten boxes that change nothing on their
     * page. A field the theme did not author is one the skin may not render, so
     * the form shows what the theme wrote and lets the seller rewrite it.
     */
    if (!(field.id in settings)) continue;
    fields.push({
      id: field.id,
      label: field.label,
      ...(field.helpText ? { helpText: field.helpText } : {}),
      multiline: field.type === "textarea",
      ...(field.validate?.max !== undefined ? { max: field.validate.max } : {}),
    });
  }
  return fields;
}

/** Whether a section has anything to edit — drives the row's Edit text control. */
export function hasCopy(section: CopySection): boolean {
  return copyFieldsFor(section).length > 0;
}

/**
 * The stored value for one field in one locale.
 *
 * A missing field reads as EMPTY rather than falling back to the other locale.
 * Showing the English line inside the Arabic box makes an untranslated field
 * look filled in, and the first save would then copy English into Arabic.
 */
export function readCopy(
  section: CopySection,
  fieldId: string,
  locale: "ar" | "en",
): string {
  const value = section.settings?.[fieldId];
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const localised = (value as Record<string, unknown>)[locale];
    return typeof localised === "string" ? localised : "";
  }
  return "";
}

/**
 * A copy of `sections` with one field written, both locales preserved.
 *
 * Writes the WHOLE `{ ar, en }` pair every time, seeded from what is stored, so
 * editing the English of a field that was only ever authored as a bare string
 * does not silently drop the Arabic — or leave a shape the storefront's
 * `sectionText` cannot read.
 */
export function writeCopy<T extends CopySection>(
  sections: T[],
  index: number,
  fieldId: string,
  locale: "ar" | "en",
  value: string,
): T[] {
  const section = sections[index];
  if (!section) return sections;
  const pair = {
    ar: readCopy(section, fieldId, "ar"),
    en: readCopy(section, fieldId, "en"),
    [locale]: value,
  };
  const next = [...sections];
  next[index] = {
    ...section,
    settings: { ...(section.settings ?? {}), [fieldId]: pair },
  };
  return next;
}

/** A yes/no setting the seller may flip — the artboard's pill switches. */
export interface SectionToggleField {
  id: string;
  label: { ar: string; en: string };
  helpText?: { ar: string; en: string };
  value: boolean;
}

/**
 * The boolean settings a section currently offers.
 *
 * The twin of `copyFieldsFor`, and filtered by the same three rules: the skin
 * has to read it, the theme has to have authored it, and any `showIf` has to
 * hold. Without these the editor showed a seller the words inside a banner but
 * no way to turn the banner off — and the schema already gates the words on the
 * switch (`announcementText` carries `showIf: { showAnnouncement: true }`), so
 * the switch was the one control that made the rest of the group make sense.
 */
export function toggleFieldsFor(
  section: CopySection,
  layout?: string,
): SectionToggleField[] {
  const schema = getSectionSchema(section.type);
  if (!schema) return [];
  const settings = (section.settings ?? {}) as Record<string, unknown>;
  const fields: SectionToggleField[] = [];
  for (const field of schema.settings ?? []) {
    if (field.type !== "checkbox") continue;
    if (!showIfHolds(field.showIf, settings)) continue;
    if (layout && !layoutRendersField(layout, section.type, field.id)) continue;
    if (!(field.id in settings)) continue;
    fields.push({
      id: field.id,
      label: field.label,
      ...(field.helpText ? { helpText: field.helpText } : {}),
      value: settings[field.id] === true,
    });
  }
  return fields;
}

/** A copy of `sections` with one boolean setting written. */
export function writeToggle<T extends CopySection>(
  sections: T[],
  index: number,
  fieldId: string,
  value: boolean,
): T[] {
  const section = sections[index];
  if (!section) return sections;
  const next = [...sections];
  next[index] = {
    ...section,
    settings: { ...(section.settings ?? {}), [fieldId]: value },
  } as T;
  return next;
}
